import { useIsFocused } from '@react-navigation/native'
import dotProp from 'dot-prop-immutable'
import gql from 'graphql-tag'
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { ActivityIndicator, FlatList, type ListRenderItemInfo, TouchableOpacity, View, useWindowDimensions } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { HStack, Text, VStack } from 'react-stacked'

import { GetRestaurantReceiptListDataDocument, type GetRestaurantReceiptListDataQuery, type GetRestaurantReceiptListDataQueryVariables, type OrderFilter, type RestaurantReceiptListOrderFragment } from '../../types/graphql'
import { PrimaryButton, SecondaryButton } from '../components/Buttons'
import Dialog, { Action } from '../components/Dialog'
import Layout, { ScreenType } from '../components/Layout'
import ListContainer from '../components/ListContainer'
import ListEmptyComponent from '../components/ListEmptyComponent'
import ReceiptSearchDialog, { type FormInput, schema } from '../components/ReceiptSearchDialog'
import ReceiptSummaryDialog from '../components/ReceiptSummaryDialog'
import { yupResolver } from '../lib/validation'
import ignoreAsync from '../util/ignoreAsync'
import logError from '../util/logError'
import useNavigation from '../util/useNavigation'
import useQuery from '../util/useQuery'

import ReceiptListItem from './ReceiptListItem'

function keyExtractor (order: { id: string }): string {
  return order.id
}

gql`
  fragment RestaurantReceiptListOrder on Order {
    id
    status
    grossAmount
    createdAt
    receiptViewKey

    restaurant {
      name
    }

    receipt {
      id
      humanOrderNumber
      refund
    }

    refundReceipts {
      id
      humanOrderNumber
      refund
    }
  }

  query GetRestaurantReceiptListData($restaurantId: ID!, $cursor: String, $filter: OrderFilter) {
    restaurant(id: $restaurantId) {
      id

      hasDeliveryHeroAccess
      timeZone

      orders(first: 25, after: $cursor, filter: $filter) {
        edges {
          node {
            ...RestaurantReceiptListOrder
          }
        }

        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
`

const Separator: React.FC = () => (
  <VStack paddingHorizontal={8}>
    <VStack backgroundColor='black' height={1} />
  </VStack>
)

const RestaurantReceiptList: React.FC = () => {
  const [, { restaurantId }] = useNavigation<'RestaurantReceiptList'>()

  const form = useForm<FormInput>({
    criteriaMode: 'all',
    defaultValues: {
      showingDateInterval: false,
      transactionChannels: null
    },
    mode: 'onTouched',
    resolver: yupResolver(schema),
    shouldUnregister: false
  })

  const isFocused = useIsFocused()
  const windowWidth = useWindowDimensions().width
  const [errorDialogVisible, setErrorDialogVisible] = useState(false)
  const insets = useSafeAreaInsets()
  const [filter, setFilter] = useState<OrderFilter | null>(null)
  const [showingSearchDialog, setShowingSearchDialog] = useState(false)
  const [showingReceiptSummary, setShowingReceiptSummary] = useState<string | null>(null)
  const { data, error, fetchingMore, fetchMore, loading, refetch, refetching } = useQuery<GetRestaurantReceiptListDataQuery, GetRestaurantReceiptListDataQueryVariables>(GetRestaurantReceiptListDataDocument, { handlesStaleData: 'briefly', pollInterval: 60000, variables: { restaurantId, filter } })

  const orders = data?.restaurant?.orders?.edges?.map(edge => edge.node) ?? []

  const loadMore = ignoreAsync(async () => {
    const pageInfo = data?.restaurant?.orders?.pageInfo
    if (pageInfo?.hasNextPage ?? false) {
      await fetchMore({
        updateQuery (previousResult, { fetchMoreResult }) {
          if (previousResult.restaurant?.orders == null) {
            return previousResult
          }

          const newEdges = fetchMoreResult?.restaurant?.orders?.edges

          if (newEdges == null || newEdges.length === 0) {
            return previousResult
          }

          return dotProp.set(previousResult, 'restaurant.orders', {
            __typename: previousResult.restaurant.orders.__typename,
            edges: [...previousResult.restaurant.orders.edges, ...newEdges],
            pageInfo: fetchMoreResult?.restaurant?.orders?.pageInfo
          })
        },
        variables: { restaurantId, cursor: pageInfo?.endCursor ?? null, filter }
      }).catch(logError)
    }
  })

  const handleRefresh = ignoreAsync(async () => {
    setErrorDialogVisible(false)

    try {
      await refetch()
    } catch (error) {
      logError(error)
      setErrorDialogVisible(true)
    }
  })

  const renderErrorDialog = (): React.ReactNode => {
    if (!errorDialogVisible) return null

    const actions = [
      <Action key='cancel' onPress={() => setErrorDialogVisible(false)} text='Avbryt' />,
      <Action key='retry' onPress={handleRefresh} text='Försök igen' />
    ]

    return (
      <Dialog actions={actions} maxWidth={windowWidth * 0.8} title='Ett fel inträffade' visible>
        <Text padding={16}>
          {'Misslyckades att uppdatera beställningshistoriken.\n\nVar vänlig se över din internetuppkoppling'}
        </Text>
      </Dialog>
    )
  }

  const renderEmpty = (): React.ReactElement => <ListEmptyComponent text={loading ? 'Laddar kvitton ...' : 'Inga kvitton hittades'} />

  const renderFooter = (): React.ReactElement => {
    if (data?.restaurant?.orders?.pageInfo.hasNextPage === true) {
      return (
        <TouchableOpacity
          disabled={fetchingMore}
          onPress={loadMore}
          style={{
            alignItems: 'center',
            backgroundColor: '#eee',
            justifyContent: 'center',
            minHeight: 40 + insets.bottom,
            paddingBottom: insets.bottom
          }}
        >
          {fetchingMore
            ? <ActivityIndicator />
            : <Text>Ladda fler</Text>}
        </TouchableOpacity>
      )
    }

    return <View style={{ height: insets.bottom }} />
  }

  const renderItem = (row: ListRenderItemInfo<RestaurantReceiptListOrderFragment>): React.ReactElement => {
    const handleOrderPress = (): void => {
      setShowingReceiptSummary(row.item.id)
    }

    return <ReceiptListItem onPress={handleOrderPress} order={row.item} />
  }

  const handleClearHistorySearch = (): void => {
    setFilter(null)
    form.reset()
  }

  const handleSearch = (filter: OrderFilter): void => {
    setFilter(filter)
    setShowingSearchDialog(false)
  }

  const handleSearchButtonPress = (): void => {
    setShowingSearchDialog(true)
  }

  useEffect(() => {
    if (error == null) return
    setErrorDialogVisible(true)
  }, [error])

  useEffect(() => {
    if (!isFocused) {
      setErrorDialogVisible(false)
      setShowingSearchDialog(false)
    }
    // These deps are necessary because if logged out the states of the dialogs change after isFocused is false, and so will open on the login screen
  }, [errorDialogVisible, isFocused, showingSearchDialog])

  useEffect(() => {
    // Close dialog on dismount
    return () => {
      setErrorDialogVisible(false)
      setShowingSearchDialog(false)
    }
  }, [])

  return (
    <Layout hideTitle screenType={ScreenType.List} title='Kvitton'>
      <ListContainer title='Kvitton'>
        {showingReceiptSummary == null
          ? null
          : (
            <ReceiptSummaryDialog
              onDismiss={() => setShowingReceiptSummary(null)}
              orderId={showingReceiptSummary}
              restaurantId={restaurantId}
            />
          )}

        {!showingSearchDialog
          ? null
          : (
            <ReceiptSearchDialog
              form={form}
              hasDeliveryHeroAccess={data?.restaurant?.hasDeliveryHeroAccess ?? false}
              onDismiss={() => setShowingSearchDialog(false)}
              onSubmit={handleSearch}
              timeZone={data?.restaurant?.timeZone ?? 'Europe/Stockholm'}
            />
          )}

        <HStack gap={8} justifyContent='end'>
          {filter == null ? null : <SecondaryButton icon='clear' onPress={handleClearHistorySearch} title='Ta bort sökning' />}

          <PrimaryButton icon='search' onPress={handleSearchButtonPress} title='Sök' />
        </HStack>

        {renderErrorDialog()}

        <FlatList
          data={orders}
          ItemSeparatorComponent={Separator}
          keyExtractor={keyExtractor}
          ListEmptyComponent={renderEmpty}
          ListFooterComponent={renderFooter}
          onRefresh={handleRefresh}
          refreshing={loading || refetching}
          renderItem={renderItem}
          style={{ flex: 1, backgroundColor: 'white' }}
        />
      </ListContainer>
    </Layout>
  )
}

export default RestaurantReceiptList
